import { initialValues, normalizeSettings } from "./partials/settings-form/default-values";
import { createRoot } from "react-dom/client";
import React from "react";
import OpenSearchSettingsForm from "./partials/settings-form/OpenSearchSettingsForm";
import OpenSearchImportSettingsForm from "./partials/settings-form/OpenSearchImportSettingsForm";

function registerCommand(commandName, editor, instanceCallback) {
  const {Commands} = editor;

  const instance = instanceCallback(editor, instanceCallback);
  Commands.add(commandName, instance);
}

function settingsFormCommand(editor, settingsKey) {
  const formElementID = 'open-search-settings-form-wrapper';
  const modalTitle = 'Settings form';

  const instance = {
    settingsKey,
    run(editor, sender, opts = {}) {
      this.editor = editor;
      this.options = opts;
      this.target = opts.target || editor.getSelected();
      const {target} = this;

      // Normalize legacy per-mode arrays into the flat { viewMode, mapping } shape before
      // rendering, so opening an old page pre-fills the form instead of showing blank fields.
      const componentData = normalizeSettings(target.get(settingsKey)) || initialValues;
      let content = `<div id="${formElementID}"></div>`;

      editor.Modal
        .open({title: modalTitle, content})
        .getModel()
        .once('change:open', () => editor.stopCommand(this.id));

      const reactRootElement = document.getElementById(formElementID);

      this._reactRoot = createRoot(reactRootElement);
      this._reactRoot.render(<OpenSearchSettingsForm
                        defaultValues={componentData}
                        submitHandler={(formValues) => {
                          const {editor, target} = this;
                          target.addAttributes({'settings': formValues});
                          target.set(settingsKey, formValues);

                          editor.Modal.close();
                        }}/>);
    },
    stop() {
      editor.Modal.close();
      if (this._reactRoot) {
        this._reactRoot.unmount();
        this._reactRoot = null;
      }
    }
  };
  return instance;
}

function exportConfigCommand(editor, settingsKey) {
  const initialValues = {};
  const formElementID = 'open-search-export-settings-wrapper';
  const modalTitle = 'Export configuration';
  const instance = {
    settingsKey,
    run(editor, sender, opts = {}) {
      this.editor = editor;
      this.options = opts;
      this.target = opts.target || editor.getSelected();
      const {target} = this;

      const componentData = target.get(settingsKey) || initialValues;
      let content = `
      <div id="${formElementID}" class="open-search-settings-form">
        <textarea>${JSON.stringify(componentData)}</textarea>
        <button class="btn btn-info btn-sm mt-3 mb-1" type="button">Download</button>
      </div>
      `;

      editor.Modal
        .open({title: modalTitle, content})
        .getModel()
        .once('change:open', () => editor.stopCommand(this.id));

      const reactRootElement = document.getElementById(formElementID);
      const btn = reactRootElement.querySelector('button');
      btn.addEventListener('click', function (evt) {
        evt.preventDefault();
        evt.stopPropagation();
        const json = JSON.stringify(componentData, null, 2);
        const blob = new Blob([json], { type: 'application/json' });

        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = 'data.json';
        document.body.appendChild(a);
        a.click();

        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      });
    },
    stop() {
      editor.Modal.close();
    }
  };
  return instance;
}

function importConfigCommand(editor, settingsKey) {
  const formElementID = 'open-search-settings-form-wrapper';
  const modalTitle = 'Import configuration';

  const instance = {
    settingsKey,
    run(editor, sender, opts = {}) {
      this.editor = editor;
      this.options = opts;
      this.target = opts.target || editor.getSelected();
      const {target} = this;

      const componentData = {
        settings: '',
      }
      let content = `<div id="${formElementID}" class="open-search-settings-form import-settings"></div>`;

      editor.Modal
        .open({title: modalTitle, content})
        .getModel()
        .once('change:open', () => editor.stopCommand(this.id));

      const reactRootElement = document.getElementById(formElementID);

      this._reactRoot = createRoot(reactRootElement);
      this._reactRoot.render(
        <OpenSearchImportSettingsForm
          defaultValues={componentData}
          submitHandler={(formValues) => {
            try {
              const {editor, target} = this;
              // Normalize on import too, so legacy-format exports don't propagate the old
              // shape into storage through an export/import cycle (read-time normalization
              // happens in settingsFormCommand, but storage should stay uniform).
              const settings = normalizeSettings(JSON.parse(formValues.settings));

              target.addAttributes({'settings': settings});
              target.set(settingsKey, settings);

              editor.Modal.close();
            } catch (e) {
              alert(`Malformed JSON. Reason: ${e.message}`);
            }
          }}/>);
    },
    stop() {
      editor.Modal.close();
      if (this._reactRoot) {
        this._reactRoot.unmount();
        this._reactRoot = null;
      }
    }
  };
  return instance;
}

export default function loadCommands(editor, options) {
  const {settingsKey, commands} = options;
  const {configure, exportConfig, importConfig} = commands;

  registerCommand(configure, editor, (editor) => settingsFormCommand(editor, settingsKey));
  registerCommand(exportConfig, editor, (editor) => exportConfigCommand(editor, settingsKey));
  registerCommand(importConfig, editor, (editor) => importConfigCommand(editor, settingsKey));
};
